/**
 * =============================================================================
 * apiEnvelope — read a response whether or not it carries the envelope
 * =============================================================================
 *
 * The Backend API Contract requires every JSON endpoint to answer with
 * `{ success, message, error, data }`. The DPA API honours that on its newer
 * controllers and not on its older ones — `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`
 * tracks which is which, and the Research controller was measured as
 * NON-conforming on 2026-10-09 and appears to have been corrected since.
 *
 * That migration is exactly the moment a screen breaks: the list payload moves
 * from the body root to `body.data`, every consumer still reads `body.items`,
 * and the table renders its headers with no rows beneath them. No error, no
 * failed request — a 200 with data in it, and an empty screen.
 *
 * These helpers read either shape, so a controller can be wrapped or unwrapped
 * without the screens caring which it is on any given day.
 *
 * THIS IS NOT A COMPATIBILITY SHIM FOR A BROKEN ENDPOINT. The rule in
 * `.claude/skills/api-contract` is that a client conforms to the contract
 * rather than bending to fit a server that does not. Unwrapping `data` IS the
 * contract — §3 defines the envelope. What is tolerated here is the *absence*
 * of the envelope on controllers that have not been migrated yet, which is a
 * documented, temporary state of this API rather than a shape anyone designed.
 */

/**
 * True when a body looks like the four-field envelope rather than the payload.
 *
 * `success` is the discriminator because it is the one field the contract makes
 * mandatory on every response, success and failure alike. A bare list payload
 * (`{ items, totalCount, … }`) has no `success`, so the two are unambiguous.
 *
 * @param {*} body
 * @returns {boolean}
 */
const isEnvelope = (body) =>
  Boolean(body) &&
  typeof body === 'object' &&
  !Array.isArray(body) &&
  typeof body.success === 'boolean';

/**
 * The payload of a response, with the envelope removed when there is one.
 *
 * @param {*} body  an axios `response.data`
 * @returns {*}     the payload, or the body unchanged when it is not wrapped
 */
export const unwrapEnvelope = (body) => (isEnvelope(body) ? body.data : body);

/**
 * A paginated list, normalised to the shape the tables already expect.
 *
 * Tolerates the three forms this API has actually been observed to return:
 *   { success, data: { items, totalCount, … } }   the contract
 *   { items, totalCount, pageNumber, pageSize }   the older controllers
 *   [ … ]                                         a bare array
 *
 * `currentPage` is also accepted for `pageNumber`: the contract names that
 * field `currentPage`, the DPA API returns `pageNumber`, and that disagreement
 * is recorded as item R3 in the conformance file.
 *
 * @param {*} body  an axios `response.data`
 * @returns {{items: Array, totalCount: number, pageNumber: number|null, pageSize: number|null}}
 */
export const unwrapList = (body) => {
  const payload = unwrapEnvelope(body);

  if (Array.isArray(payload)) {
    return {
      items: payload,
      totalCount: payload.length,
      pageNumber: null,
      pageSize: null,
    };
  }

  const items = Array.isArray(payload?.items) ? payload.items : [];

  return {
    items,
    // Never fall back to `items.length` for a paginated list: that would report
    // the size of the current PAGE as the size of the whole set, and the pager
    // would show one page however many there really are.
    totalCount:
      typeof payload?.totalCount === 'number' ? payload.totalCount : items.length,
    pageNumber: payload?.pageNumber ?? payload?.currentPage ?? null,
    pageSize: payload?.pageSize ?? null,
  };
};

/**
 * A single entity, normalised to an ARRAY of at most one.
 *
 * The research detail screens read `data[0]`, because this API answers
 * get-by-id with an array of one rather than the bare entity the contract asks
 * for (conformance item R4). Returning an array here keeps those screens
 * working whichever way the endpoint is answering today.
 *
 * @param {*} body  an axios `response.data`
 * @returns {Array}
 */
export const unwrapEntityList = (body) => {
  const payload = unwrapEnvelope(body);

  if (Array.isArray(payload)) return payload;
  if (payload === null || payload === undefined) return [];
  return [payload];
};

export default unwrapList;
